import axiosClient from "utils/axios";
import { ApiList, ApiOptionList, ApiResponse } from "../../../types/api.types";
import {
  AddWorkOrderRequest,
  CreateWorkOrderTaskRequest,
  WorkOrder,
  WorkOrderTask,
  UpdateWorkOrderItemQuantitiesRequest,
  UpdateWorkOrderTaskRequest,
  ScheduleWorkOrderPayload,
  UpdateWorkOrderRequest,
  WorkOrderFinancials,
  WorkOrderReportRow,
} from "features/WorkOrders/types/work-order.types";

/** Query shape for `GET /work-orders` (pagination, search, and server-side filters). */
export type WorkOrderListParams = {
  page?: number;
  limit?: number;
  search?: string;
  /** Comma-separated {@link WorkOrderStatus} values */
  status?: string;
  /** Comma-separated {@link WorkOrderSourceType} values */
  sourceType?: string;
  sourceId?: number;
  createdBy?: number;
  createdFrom?: string;
  createdTo?: string;
};

function buildWorkOrderListQueryParams(
  input: WorkOrderListParams,
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  const setPositiveInt = (key: string, v: number | undefined) => {
    if (v != null && Number.isFinite(v) && Number(v) > 0) {
      params[key] = Number(v);
    }
  };
  const setNonEmpty = (key: string, v: string | undefined) => {
    if (v != null && String(v).trim() !== "") {
      params[key] = String(v).trim();
    }
  };

  if (input.page != null) params.page = input.page;
  if (input.limit != null) params.limit = input.limit;
  setNonEmpty("search", input.search);
  setNonEmpty("status", input.status);
  setNonEmpty("sourceType", input.sourceType);
  setPositiveInt("sourceId", input.sourceId);
  setPositiveInt("createdBy", input.createdBy);
  setNonEmpty("createdFrom", input.createdFrom);
  setNonEmpty("createdTo", input.createdTo);

  return params;
}

export const getWorkOrders = async (input: WorkOrderListParams = {}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiList<WorkOrder[]>>>(
      "/work-orders",
      { params: buildWorkOrderListQueryParams(input) },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Work Orders : ", err);
    throw err;
  }
};

export const getWorkOrderReport = async (input: WorkOrderListParams = {}) => {
  const response = await axiosClient.get<
    ApiResponse<ApiList<WorkOrderReportRow[]>>
  >("/work-orders/report", { params: buildWorkOrderListQueryParams(input) });
  return response.data;
};

export const getWorkOrderOptions = async ({
  query,
  limit,
  page,
  status,
  ids,
}: {
  query?: string;
  limit?: number;
  page?: number;
  status?: string;
  ids?: string;
}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      `/work-orders/options`,
      {
        params: {
          query,
          limit,
          page,
          status: status || undefined,
          ids: ids || undefined,
        },
      },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Work Order Options : ", err);
    throw err;
  }
};

export const createWorkOrder = async (payload: AddWorkOrderRequest) => {
  try {
    const response = await axiosClient.post<ApiResponse<WorkOrder>>(
      "/work-orders",
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating Work Order : ", err);
    throw err;
  }
};

export const updateWorkOrder = async (
  id: number,
  payload: UpdateWorkOrderRequest,
) => {
  try {
    const response = await axiosClient.put<ApiResponse<UpdateWorkOrderRequest>>(
      `/work-orders/${id}`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating Work Order : ", err);
    throw err;
  }
};

export const updateWorkOrderItemQuantities = async (
  workOrderId: number,
  payload: UpdateWorkOrderItemQuantitiesRequest,
) => {
  try {
    const response = await axiosClient.put<
      ApiResponse<UpdateWorkOrderItemQuantitiesRequest>
    >(`/work-orders/${workOrderId}/items/quantities`, payload);
    return response.data;
  } catch (err) {
    console.warn("Error updating work order item quantities : ", err);
    throw err;
  }
};

export const getWorkOrder = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }

    const response = await axiosClient.get<ApiResponse<WorkOrder>>(
      `/work-orders/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Work Order : ", err);
    throw err;
  }
};

export const deleteWorkOrder = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<WorkOrder>>(
      `/work-orders/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting Work Order : ", err);
    throw err;
  }
};

export const getWorkOrderTasks = async (workOrderId: number) => {
  try {
    const response = await axiosClient.get<ApiResponse<WorkOrderTask[]>>(
      `/work-orders/${workOrderId}/tasks`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Work Order tasks : ", err);
    throw err;
  }
};

export const createWorkOrderTask = async (
  workOrderId: number,
  payload: CreateWorkOrderTaskRequest,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<WorkOrderTask>>(
      `/work-orders/${workOrderId}/tasks`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating Work Order task : ", err);
    throw err;
  }
};

export const updateWorkOrderTask = async (
  workOrderId: number,
  taskId: number,
  payload: UpdateWorkOrderTaskRequest,
) => {
  try {
    const response = await axiosClient.put<ApiResponse<WorkOrderTask>>(
      `/work-orders/${workOrderId}/tasks/${taskId}`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating Work Order task : ", err);
    throw err;
  }
};

export const deleteWorkOrderTask = async (
  workOrderId: number,
  taskId: number,
) => {
  try {
    const response = await axiosClient.delete<ApiResponse<WorkOrderTask>>(
      `/work-orders/${workOrderId}/tasks/${taskId}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting Work Order task : ", err);
    throw err;
  }
};

/** NEW (+ legacy DRAFT/ASSIGNED) → IN_PROGRESS via start. */
export const scheduleWorkOrder = async (
  id: number,
  payload?: ScheduleWorkOrderPayload,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<WorkOrder>>(
      `/work-orders/${id}/schedule`,
      payload ?? {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error scheduling work order : ", err);
    throw err;
  }
};

/** SCHEDULED → IN_PROGRESS; sets `actualStart` when unset. */
export const startWorkOrder = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<WorkOrder>>(
      `/work-orders/${id}/start`,
      {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error starting work order : ", err);
    throw err;
  }
};

/** IN_PROGRESS → ON_HOLD */
export const holdWorkOrder = async (
  id: number,
  payload?: { reason?: string | null },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<WorkOrder>>(
      `/work-orders/${id}/hold`,
      payload ?? {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error holding work order : ", err);
    throw err;
  }
};

/** ON_HOLD → IN_PROGRESS; sets `actualStart` when still unset. */
export const resumeWorkOrder = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<WorkOrder>>(
      `/work-orders/${id}/resume`,
      {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error resuming work order : ", err);
    throw err;
  }
};

/**
 * IN_PROGRESS → PENDING_COMPLETION_APPROVAL.
 * Multipart: `feedback` (string) + one or more `completionDocuments` files.
 */
export const submitWorkOrderCompletion = async (id: number, data: FormData) => {
  try {
    const response = await axiosClient.post<ApiResponse<WorkOrder>>(
      `/work-orders/${id}/submit-completion`,
      data,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  } catch (err) {
    console.warn("Error submitting work order completion : ", err);
    throw err;
  }
};

/** PENDING_COMPLETION_APPROVAL → READY_FOR_BILLING. Gated by work_order.approve. */
export const approveWorkOrderForBilling = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<WorkOrder>>(
      `/work-orders/${id}/approve-for-billing`,
      {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error approving work order for billing : ", err);
    throw err;
  }
};

/** PENDING_COMPLETION_APPROVAL → IN_PROGRESS. Requires a reason. Gated by work_order.reject. */
export const rejectWorkOrderCompletion = async (
  id: number,
  payload: { reason: string },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<WorkOrder>>(
      `/work-orders/${id}/reject-completion`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error rejecting work order completion : ", err);
    throw err;
  }
};

/**
 * READY_FOR_BILLING → COMPLETED. Gated by work_order.submit_billing.
 * Sets finalAmount, generates the invoice, closes work assignments.
 */
export const submitWorkOrderBilling = async (
  id: number,
  payload: { finalAmount?: number | null },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<WorkOrder>>(
      `/work-orders/${id}/submit-billing`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error submitting work order billing : ", err);
    throw err;
  }
};

export const getWorkOrderFinancials = async (
  id: number,
  mode?: "PROJECTED" | "ACTUAL",
) => {
  try {
    const response = await axiosClient.get<ApiResponse<WorkOrderFinancials>>(
      `/work-orders/${id}/financials`,
      { params: mode ? { mode } : undefined },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching work order financials : ", err);
    throw err;
  }
};

/** ANY active status → CANCELLED */
export const cancelWorkOrder = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<WorkOrder>>(
      `/work-orders/${id}/cancel`,
      {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error cancelling work order : ", err);
    throw err;
  }
};

/** COMPLETED → CLOSED */
export const closeWorkOrder = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<WorkOrder>>(
      `/work-orders/${id}/close`,
      {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error closing work order : ", err);
    throw err;
  }
};
